import { RowSelectionStore } from "@/components/data-table/context/RowSelectionContext";
import { useToast } from "@/components/ui/use-toast";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { EmailRecipientsService } from "@/services/emailRecipients";
import { QueryKey, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

export const useEmailRecipientsRemoveUsersMutation = ({
  store,
  emailTypeId,
  queryKey,
}: {
  store: RowSelectionStore;
  emailTypeId: string;
  queryKey: QueryKey;
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutationWithErrorHandling({
    mutationFn: (recipientUserIds: string[]) =>
      EmailRecipientsService.removeUsers(emailTypeId, recipientUserIds),
    errorTitle: t("emailRecipients.errors.remove"),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      store.clear();
      toast({
        title: t("emailRecipients.success.remove"),
      });
    },
  });

  const handleSubmit = useCallback(async () => {
    const selectedUserIds = [...store.getSelection()];
    if (selectedUserIds.length === 0) return;
    await mutation.mutateAsync(selectedUserIds);
  }, [mutation, store]);

  return {
    handleSubmit,
    isSubmitting: mutation.isPending,
  };
};
